import { useState, useEffect, useRef, useCallback, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useCursor } from "../../context/CursorContext";
import "./contact.css";

export default function Contact() {
  const navigate = useNavigate();
  const { setIsLightCursor } = useCursor();
  const isDesktop = useRef(typeof window !== "undefined" && window.innerWidth >= 1024);
  const [slideIn, setSlideIn] = useState(false);
  const [exiting, setExiting] = useState(false);
  const exiting_ref = useRef(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setSlideIn(true);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleBack = useCallback(() => {
    if (exiting_ref.current) return;
    exiting_ref.current = true;
    setIsLightCursor(false);
    setExiting(true);
    setTimeout(() => navigate("/"), 750);
  }, [navigate, setIsLightCursor]);

  useEffect(() => {
    const handler = () => handleBack();
    window.addEventListener("closeContact", handler);
    return () => window.removeEventListener("closeContact", handler);
  }, [handleBack]);

  /* ── Overlay animation ── */
  const overlayStyle: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    overflowY: "auto",
    ...(isDesktop.current
      ? {
          transform: slideIn && !exiting ? "translateY(0)" : "translateY(100vh)",
          transition: "transform 0.75s cubic-bezier(0.76, 0, 0.24, 1)",
          willChange: "transform",
        }
      : {
          transform: slideIn && !exiting ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.75s cubic-bezier(0.76, 0, 0.24, 1)",
          willChange: "transform",
        }),
  };

  return (
    <main
      className="flex flex-col lg:h-screen lg:overflow-hidden"
      style={{ backgroundColor: "#191b1e", minHeight: "100dvh", ...overlayStyle }}
    >
      {/* ── Mobile Header — logo only ── */}
      <div className="lg:hidden w-full h-[220px] flex items-end justify-center pb-10 px-6 shrink-0">
        <img
          src="https://static.readdy.ai/image/413ccbf5fb2d9cba47392df0340806cc/43f6059700bc995517da5a725b32f520.png"
          alt="Logo"
          className="h-[48px] w-auto object-contain"
        />
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">

        {/* ── LEFT PANEL — desktop only ── */}
        <div
          className="hidden lg:flex flex-col items-end justify-between w-[480px] xl:w-[520px] shrink-0 pt-8 pr-10"
          style={{ paddingBottom: "clamp(110px, calc(50vh - 360px), 400px)" }}
        >
          <div className="flex-1 flex flex-col items-end justify-center gap-20">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); handleBack(); }}
              className="cursor-pointer flex justify-end"
            >
              <img
                src="https://static.readdy.ai/image/413ccbf5fb2d9cba47392df0340806cc/43f6059700bc995517da5a725b32f520.png"
                alt="Logo"
                className="object-contain"
                style={{ width: "200px" }}
              />
            </a>
            <button
              onClick={handleBack}
              className="contact-back-btn text-xs cursor-pointer bg-transparent border-none outline-none whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, letterSpacing: "normal" }}
            >
              ← voltar
            </button>
          </div>
          {/* Footer text */}
          <div className="text-right leading-relaxed">
            <p style={{ color: "#eaeaeb", fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 400 }}>© ECOS 3D</p>
            <p style={{ color: "#eaeaeb", fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 400 }}>Todos os direitos reservados</p>
          </div>
        </div>

        {/* ── CONTACT CONTENT ── */}
        <section className="flex-1 flex items-center lg:items-start justify-center px-6 pb-20 lg:pb-0 lg:pt-[clamp(80px,calc(50vh-160px),340px)]">
          <div
            className="flex flex-col items-center text-center gap-10"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, color: "#eaeaeb" }}
          >
            {/* Description */}
            <p
              className="text-base leading-relaxed max-w-xs"
              style={{ letterSpacing: "0.02em", color: "#eaeaeb" }}
            >
              Estúdio de visualização de arquitetura sediado em Aveiro.
              <br />
              Disponível para novos projetos.
            </p>

            {/* Divider */}
            <span className="block w-6 border-t border-stone-600" />

            {/* Links */}
            <div className="flex flex-col items-center gap-4">
              <a
                href="mailto:ecos3d.studio@gmail.com"
                className="contact-link text-sm"
                style={{ letterSpacing: "0.10em" }}
              >
                ecos3d.studio@gmail.com
              </a>

              <a
                href="tel:+351917964536"
                className="contact-link text-sm"
                style={{ letterSpacing: "0.10em" }}
              >
                +351 917 964 536
              </a>

              <p
                className="text-sm"
                style={{ letterSpacing: "0.05em", color: "#eaeaeb" }}
              >
                Rua do Carmo nº26, 3800-128 Aveiro
              </p>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-8">
              <a
                href="https://www.instagram.com/ecos.3d?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link w-5 h-5 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-instagram-line" style={{ fontSize: "18px", color: "inherit" }} />
              </a>
              <a
                href="https://behance.net"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link w-5 h-5 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-behance-line" style={{ fontSize: "18px", color: "inherit" }} />
              </a>
            </div>
          </div>
        </section>

      </div>
    </main>
  );
}
